// Taken from https://github.com/remix-run/react-router/blob/f54c409d26298fb72885fab2748474d43ff2052c/packages/react-router/lib/dom/lib.tsx#L256

import { action, atom, batch, reatomArray } from '@reatom/framework'
import { useCtx } from '@reatom/npm-react'
import React from 'react'
import { parsePath, Router, UNSAFE_createBrowserHistory } from 'react-router'

import { rootCtx } from '../reatom/root-ctx'

import type { BrowserRouterProps, Location, To } from 'react-router'

export type BrowserHistory = ReturnType<typeof UNSAFE_createBrowserHistory>

export const browserHistory = UNSAFE_createBrowserHistory({
  window,
  v5Compat: true,
})

type HistoryListener = Parameters<BrowserHistory['listen']>[0]
type Update = Parameters<HistoryListener>[0]

const historyListeners = new Set<HistoryListener>()
browserHistory.listen((location) => {
  for (const listener of historyListeners) {
    listener(location)
  }
})

export const addBrowserHistoryListener = (listener: HistoryListener) => {
  historyListeners.add(listener)
}
export const removeBrowserHistoryListener = (listener: HistoryListener) => {
  historyListeners.delete(listener)
}

export const routerHistoryStackAtom = reatomArray<Location>(
  [browserHistory.location],
  'routerHistoryStackAtom',
)

export const routerLocationAtom = atom(
  browserHistory.location,
  'routerLocationAtom',
)

export const routerActions = {
  /**
   * If router location is already 'to' do nothing, push otherwise
   */
  navigate: action((ctx, to: To) => {
    const location = ctx.get(routerLocationAtom)
    const toParsed = typeof to === 'string' ? parsePath(to) : to
    if (
      location.pathname === toParsed.pathname &&
      location.search === toParsed.search
    ) {
      return
    }
    browserHistory.push(to as string)
  }, 'routeActions.navigate'),
}

export const lastVisitedRouterLocationAtom = atom((ctx) => {
  const history = ctx.spy(routerHistoryStackAtom)
  return history.at(1)
}, 'lastVisitedRouterLocationAtom')

const handleUpdate = (update: Update) => {
  batch(rootCtx, () => {
    routerLocationAtom(rootCtx, update.location)
    if (update.action === 'PUSH') {
      routerHistoryStackAtom.unshift(rootCtx, update.location)
    }
    if (update.action === 'POP') {
      routerHistoryStackAtom.shift(rootCtx)
    }
    if (update.action === 'REPLACE') {
      routerHistoryStackAtom.shift(rootCtx)
      routerHistoryStackAtom.unshift(rootCtx, update.location)
    }
  })
}

historyListeners.add(handleUpdate)

/**
 * A `<Router>` for use in web browsers. Provides the cleanest URLs.
 *
 * @category Component Routers
 */
export function BrowserRouter({
  basename,
  children,
  window: _,
}: BrowserRouterProps) {
  const history = browserHistory
  const ctx = useCtx()
  const [state, setStateImpl] = React.useState({
    action: history.action,
    location: history.location,
  })
  // Route transitions must commit at normal priority — wrapping in startTransition
  // visibly delays navigation when the main thread is busy (e.g. virtualized lists).
  const setState = setStateImpl

  React.useLayoutEffect(() => {
    const handleUpdate = (update: Update) => {
      setState(update)
    }

    historyListeners.add(handleUpdate)
    return () => {
      historyListeners.delete(handleUpdate)
    }
  }, [ctx, history, setState])

  return (
    <Router
      basename={basename}
      location={state.location}
      navigationType={state.action}
      navigator={history}
    >
      {children}
    </Router>
  )
}
